import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, X } from 'lucide-react';
import Header from './components/Header.tsx';
import VideoGrid from './components/VideoGrid.tsx';
import Controls from './components/Controls.tsx';
import SettingsModal from './components/SettingsModal.tsx';
import ParticipantsPanel from './components/ParticipantsPanel.tsx';
import ChatPanel from './components/ChatPanel.tsx';
import AgendaPanel from './components/AgendaPanel.tsx';
import PreJoin from './components/PreJoin.tsx';
import ReactionBar from './components/ReactionBar.tsx';
import FloatingReactions from './components/FloatingReactions.tsx';
import BackgroundEffectsModal from './components/BackgroundEffectsModal.tsx';
import type { VideoBackgroundEffect } from './types/meeting.types.ts';
import { useMediaStream } from './hooks/useMediaStream.ts';
import { useWebRTC } from './hooks/useWebRTC.ts';
import { useAdaptiveVideoQuality } from './hooks/useAdaptiveVideoQuality.ts';
import type { VideoQualityPreference, VideoQualityTier } from './utils/videoQuality.ts';
import { useMeetingTimeLimit } from './hooks/useMeetingTimeLimit.ts';
import { useMeetingRecording } from './hooks/useMeetingRecording.ts';
import { useMeetingAgenda } from './hooks/useMeetingAgenda.ts';
import { useMeetingMinutes } from './hooks/useMeetingMinutes.ts';
import { useVideoBackgroundEffect } from './hooks/useVideoBackgroundEffect.ts';
import { authService } from '../../services/auth.service.ts';
import { meetingService } from '../../services/meeting.service.ts';
import { saveSummaryForMeeting } from '../../services/summaries.service.ts';
import { FREE_MEETING_MAX_DURATION_MINUTES } from '../../utils/plan.ts';
import { Participant, MeetingTopic } from './types/meeting.types.ts';
import type { AgendaTimelineSync } from './utils/agendaTimeline.ts';
import { buildMeetingMinutes } from './utils/buildMeetingMinutes.ts';
import { getParticipantScreenShareState } from './utils/mediaUtils.ts';
import { readRoomTimePolicy } from './utils/roomTimePolicy.ts';

const Meeting = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Kayıtlı kullanıcı veya misafir bilgisini al
  const registeredUser = authService.getUser();
  const guestData = sessionStorage.getItem('tbkmeet_guest');
  
  let userName = 'Anonim';
  let isGuest = false;
  
  if (registeredUser) {
    userName = registeredUser.name;
  } else if (guestData) {
    try {
      const guest = JSON.parse(guestData);
      userName = guest.name;
      isGuest = true;
    } catch {
      // Parse hatası - anonim olarak devam et
    }
  }
  
  const [showParticipants, setShowParticipants] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [meetingTopics, setMeetingTopics] = useState<MeetingTopic[]>([]);
  const [meetingTitle, setMeetingTitle] = useState('Toplantı');
  const applyRemoteAgendaRef = useRef<(sync: AgendaTimelineSync) => void>(() => {});
  const applyRemoteMinutesRef = useRef<(enabled: boolean) => void>(() => {});
  const saveMinutesOnLeaveRef = useRef<() => Promise<void>>(async () => {});
  const leavingRef = useRef(false);
  const minutesSavedRef = useRef(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showBackgroundEffects, setShowBackgroundEffects] = useState(false);
  const [videoBackgroundEffect, setVideoBackgroundEffect] = useState<VideoBackgroundEffect>('none');
  const [transparentMode, setTransparentMode] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showTimeExpiredModal, setShowTimeExpiredModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoQualityPreference, setVideoQualityPreference] = useState<VideoQualityPreference>('auto');
  const [autoVideoQuality, setAutoVideoQuality] = useState<VideoQualityTier>('high');
  const effectiveVideoQuality =
    videoQualityPreference === 'auto' ? autoVideoQuality : videoQualityPreference;
  // Kamera yakalama kalitesi otomatik kademe değişimlerinden ETKİLENMEZ:
  // aksi halde her kademe geçişinde getUserMedia yenilenir ve görüntü titrer.
  // Otomatik uyum yalnızca WebRTC gönderim parametrelerinde (bitrate/ölçek) yapılır.
  const cameraCaptureQuality: VideoQualityTier =
    videoQualityPreference === 'auto' ? 'high' : videoQualityPreference;
  const [selectedParticipantId, setSelectedParticipantId] = useState<string>('local');
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  // Ön izleme (pre-join): kullanıcı "Katıl"a basana kadar odaya bağlanılmaz
  const [hasJoined, setHasJoined] = useState(false);
  const meetingContainerRef = useRef<HTMLDivElement>(null);
  const videoAreaRef = useRef<HTMLDivElement>(null);
  
  const remoteVideoRefs = useRef<{ [key: string]: React.RefObject<HTMLVideoElement> }>({});

  const {
    localVideoRef,
    localScreenVideoRef,
    localCameraPreviewRef,
    localStreamRef,
    localCameraStream,
    localScreenStream,
    availableCameras,
    availableMicrophones,
    selectedCamera,
    selectedMicrophone,
    isVideoEnabled,
    isAudioEnabled,
    isScreenSharing,
    toggleVideo,
    toggleAudio,
    changeCamera,
    changeMicrophone,
    toggleScreenShare,
    stopStream,
    setOnTrackChange
  } = useMediaStream(cameraCaptureQuality);

  // Stream hazır olduğunda state'e kaydet
  useEffect(() => {
    const checkStream = setInterval(() => {
      if (localStreamRef.current && !localStream) {
        console.log('Local stream ready!');
        setLocalStream(localStreamRef.current);
        clearInterval(checkStream);
      }
    }, 100);
    return () => clearInterval(checkStream);
  }, [localStreamRef, localStream]);

  // Sessiz çıkış: toplantıyı sonlandırmadan sadece bu kullanıcıyı çıkarır.
  const performLeave = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    void (async () => {
      try {
        await saveMinutesOnLeaveRef.current();
      } catch {
        /* tutanak kaydı başarısız olsa da çıkışa devam */
      }
      stopStream();
      if (isGuest) {
        sessionStorage.removeItem('tbkmeet_guest');
        navigate('/');
      } else {
        navigate('/dashboard');
      }
    })();
  }, [stopStream, isGuest, navigate]);

  // Sayfa değişince veya sekme kapanınca kamera/mikrofonu bırak
  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  const handleMeetingEnded = useCallback(() => {
    alert('Toplantı sonlandırıldı.');
    performLeave();
  }, [performLeave]);

  const handleTimeExpired = useCallback(() => {
    setShowTimeExpiredModal(true);

    if (!isGuest && id) {
      void (async () => {
        try {
          const meeting = await meetingService.getMeetingByCode(id);
          if (meeting?.id) {
            await meetingService.expireLiveMeeting(meeting.id);
          }
        } catch {
          try {
            const meeting = await meetingService.getMeetingByCode(id);
            if (meeting?.id) {
              await meetingService.deleteMeeting(meeting.id);
            }
          } catch {
            /* sunucu zaten silmiş olabilir */
          }
        }
      })();
    }
  }, [isGuest, id]);

  const {
    policy,
    elapsedSeconds,
    remainingSeconds,
    isLimited,
    showWarning,
    applyRemotePolicy,
    roomTimePolicyForSocket,
  } = useMeetingTimeLimit(id, isGuest, handleTimeExpired);

  // Host yalnızca toplantının gerçek sahibi (ownerId) olabilir.
  // 'pending': sahiplik henüz doğrulanmadı (bu sırada kimse host iddia etmez)
  // 'fallback': toplantının backend kaydı yok (P2P) → eski davranış, ilk kayıtlı kullanıcı üstlenir
  const [meetingOwnership, setMeetingOwnership] = useState<'pending' | 'owner' | 'not-owner' | 'fallback'>('pending');
  const registeredUserId = registeredUser?.id;

  useEffect(() => {
    if (!id) {
      setMeetingOwnership('not-owner');
      setMeetingTopics([]);
      return;
    }
    if (isGuest || !registeredUserId) {
      setMeetingOwnership('not-owner');
    }
    let cancelled = false;
    (async () => {
      try {
        const meeting = await meetingService.getMeetingByCode(id);
        if (cancelled) return;
        const topics = (meeting?.topics || [])
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((t, index) => ({
            id: t.id || `topic-${index}`,
            title: t.title,
            duration: t.duration,
            type: t.type || 'bilgilendirme',
            order: t.order ?? index,
          }));
        setMeetingTopics(topics);
        if (meeting?.title) setMeetingTitle(meeting.title);

        if (isGuest || !registeredUserId) return;
        const ownerId = meeting?.ownerId || meeting?.owner?.id;
        if (ownerId) {
          setMeetingOwnership(ownerId === registeredUserId ? 'owner' : 'not-owner');
        } else {
          setMeetingOwnership('fallback');
        }
      } catch {
        if (!cancelled) {
          if (!isGuest && registeredUserId) setMeetingOwnership('fallback');
          setMeetingTopics([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isGuest, registeredUserId]);

  const isOwnerLike = !isGuest
    && (meetingOwnership === 'owner' || meetingOwnership === 'fallback');

  // Bekleme odası ayarı (toplantı sahibi belirler; sayfa yenilense de korunur)
  const waitingRoomStorageKey = `tbkmeet_waiting_room_${id || ''}`;
  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState<boolean>(
    () => sessionStorage.getItem(waitingRoomStorageKey) === '1'
  );
  const handleWaitingRoomChange = useCallback(
    (enabled: boolean) => {
      setWaitingRoomEnabled(enabled);
      try {
        sessionStorage.setItem(waitingRoomStorageKey, enabled ? '1' : '0');
      } catch {
        /* depolama kullanılamıyor olabilir */
      }
    },
    [waitingRoomStorageKey]
  );

  const claimHost = isOwnerLike
    && (
      !!roomTimePolicyForSocket
      || !!readRoomTimePolicy(id || '')
      || waitingRoomEnabled
    );

  // Host tarafı: onay bekleyen katılım istekleri
  const [pendingJoinRequests, setPendingJoinRequests] = useState<
    Array<{ socketId: string; userName: string }>
  >([]);
  const handleJoinRequest = useCallback((socketId: string, requestUserName: string) => {
    setPendingJoinRequests((prev) => {
      if (prev.some((r) => r.socketId === socketId)) return prev;
      return [...prev, { socketId, userName: requestUserName }];
    });
  }, []);

  const handleForceMute = useCallback(() => {
    if (isAudioEnabled) {
      toggleAudio();
    }
  }, [isAudioEnabled, toggleAudio]);

  const handleKicked = useCallback(() => {
    alert('Toplantı sahibi sizi toplantıdan çıkardı.');
    performLeave();
  }, [performLeave]);

  useEffect(() => {
    if (!showTimeExpiredModal) return;
    const timer = window.setTimeout(() => performLeave(), 12000);
    return () => window.clearTimeout(timer);
  }, [showTimeExpiredModal, performLeave]);

  // WebRTC hook
  const {
    participants: remoteParticipants,
    isConnected,
    replaceTrack,
    collectNetworkMetrics,
    mySocketId,
    hostSocketId,
    isHost,
    chatMessages,
    sendChatMessage,
    chatEncryptionEnabled,
    mediaEncryptionEnabled,
    e2eRoomActive,
    e2eReadyPeers,
    participantMediaState,
    broadcastMediaState,
    muteParticipant,
    kickParticipant,
    endMeetingForAll,
    participantSignals,
    floatingReactions,
    sendReaction,
    broadcastHandRaise,
    broadcastAgendaTimeline,
    broadcastMinutesPolicy,
    lobbyStatus,
    respondToJoinRequest,
  } = useWebRTC(
    hasJoined ? id || '' : '',
    userName,
    localStream,
    effectiveVideoQuality,
    {
      policy: roomTimePolicyForSocket,
      onRemotePolicy: applyRemotePolicy,
      claimHost,
      joinMode: isOwnerLike ? 'direct' : 'lobby',
      requireJoinApproval: isOwnerLike && waitingRoomEnabled,
      canManageLobby: isOwnerLike && waitingRoomEnabled,
      onJoinRequest: handleJoinRequest,
      onForceMute: handleForceMute,
      onKicked: handleKicked,
      onMeetingEnded: handleMeetingEnded,
      onRemoteAgendaTimeline: (sync) => applyRemoteAgendaRef.current(sync),
      onRemoteMinutesPolicy: (enabled) => applyRemoteMinutesRef.current(enabled),
    }
  );

  const remoteAudioStreams = useMemo(
    () =>
      remoteParticipants
        .map((p) => p.stream)
        .filter((s): s is MediaStream => !!s && s.getAudioTracks().length > 0),
    [remoteParticipants]
  );

  const {
    minutesEnabled,
    isTranscribing,
    transcript: minutesTranscript,
    sttAvailable,
    lastError: minutesLastError,
    enableMinutes,
    disableMinutes,
    applyRemoteMinutesPolicy,
    flushPendingTranscription,
  } = useMeetingMinutes({
    meetingId: id,
    isHost,
    localStream,
    remoteAudioStreams,
    hostDisplayName: userName,
    broadcastMinutesPolicy,
  });
  applyRemoteMinutesRef.current = applyRemoteMinutesPolicy;

  const [showMinutesConfirm, setShowMinutesConfirm] = useState(false);
  const [showMinutesBanner, setShowMinutesBanner] = useState(false);

  useEffect(() => {
    if (!minutesEnabled) {
      setShowMinutesBanner(false);
      return;
    }
    setShowMinutesBanner(true);
    const timer = window.setTimeout(() => setShowMinutesBanner(false), 5000);
    return () => window.clearTimeout(timer);
  }, [minutesEnabled]);

  const handleToggleMinutes = useCallback(() => {
    if (!isHost) return;
    if (minutesEnabled) {
      disableMinutes();
      return;
    }
    setShowMinutesConfirm(true);
  }, [isHost, minutesEnabled, disableMinutes]);

  const confirmEnableMinutes = useCallback(() => {
    setShowMinutesConfirm(false);
    enableMinutes();
  }, [enableMinutes]);

  saveMinutesOnLeaveRef.current = async () => {
    if (!isHost || !id || !minutesEnabled || minutesSavedRef.current) return;
    minutesSavedRef.current = true;
    const latestTranscript = await flushPendingTranscription();
    const participantNames = [
      userName,
      ...remoteParticipants.map((p) => p.userName),
    ].filter(Boolean);
    const startedAt =
      policy?.startedAt ?? Date.now() - Math.max(elapsedSeconds, 1) * 1000;
    const summary = buildMeetingMinutes({
      meetingId: id,
      meetingCode: id,
      title: meetingTitle,
      hostName: userName,
      participants: Array.from(new Set(participantNames)),
      startedAt,
      endedAt: Date.now(),
      agenda: meetingTopics.map((t) => ({
        title: t.title,
        type: t.type,
        duration: t.duration,
        order: t.order,
      })),
      transcript: latestTranscript.length > 0 ? latestTranscript : minutesTranscript,
      minutesEnabled: true,
    });
    summary.id = `summary_${id}`;
    await saveSummaryForMeeting(summary);
  };

  const {
    hasAgenda,
    segments: agendaSegments,
    resolved: agendaResolved,
    warnSoon: agendaWarnSoon,
    remainingSec: agendaRemainingSec,
    currentTopic: agendaCurrentTopic,
    nextTopic: agendaNextTopic,
    canAdvance: canAdvanceAgenda,
    advanceAgenda,
    applyRemoteSync: applyRemoteAgendaSync,
  } = useMeetingAgenda({
    topics: meetingTopics,
    isHost,
    isConnected,
    fallbackStartedAt: policy?.startedAt ?? null,
    broadcastAgendaTimeline,
  });
  applyRemoteAgendaRef.current = applyRemoteAgendaSync;

  const handleRespondJoinRequest = useCallback(
    (socketId: string, approved: boolean) => {
      respondToJoinRequest(socketId, approved);
      setPendingJoinRequests((prev) => prev.filter((r) => r.socketId !== socketId));
    },
    [respondToJoinRequest]
  );

  // Host odada mı? (Host hiç belirlenmediyse kimse "host yok" yetkisi kazanmasın)
  const hostPresent = !hostSocketId
    || hostSocketId === mySocketId
    || remoteParticipants.some((p) => p.socketId === hostSocketId);

  // Host ayrılırsa toplantı herkes için biter.
  // Host odada değilse (ör. bağlantısı koptuysa) kalan katılımcı da bitirebilir.
  const canEndMeetingForAll = isHost || !hostPresent;

  const confirmLeave = useCallback(() => {
    if (canEndMeetingForAll) {
      endMeetingForAll();

      // Sunucu tarafında da kapat: anlık toplantı silinir, planlı toplantı ENDED olur.
      // (Endpoint'ler sahiplik kontrolü yapar; yetkimiz yoksa sessizce geçilir.)
      if (!isGuest && id) {
        void (async () => {
          try {
            const meeting = await meetingService.getMeetingByCode(id);
            if (meeting?.id && meeting.status === 'LIVE') {
              if (meeting.isInstant) {
                await meetingService.expireLiveMeeting(meeting.id);
              } else {
                await meetingService.endMeeting(meeting.id);
              }
            }
          } catch {
            /* sahibi değiliz veya toplantı zaten kapanmış */
          }
        })();
      }
    }

    performLeave();
  }, [canEndMeetingForAll, endMeetingForAll, isGuest, id, performLeave]);

  const {
    isRecording,
    isSaving: isSavingRecording,
    toggleRecording,
  } = useMeetingRecording({
    meetingId: id || '',
    containerRef: videoAreaRef,
    audioStream: localStream,
  });

  const cameraSourceStream = localCameraStream || localStream;
  const {
    stream: localDisplayStream,
    isLoading: isBackgroundEffectLoading,
    error: backgroundEffectError,
  } = useVideoBackgroundEffect(
    cameraSourceStream,
    videoBackgroundEffect,
    isVideoEnabled && !isScreenSharing && !!cameraSourceStream
  );

  // remoteParticipants.length deps'te: geç katılanlar da mevcut ekran paylaşımı durumunu alsın
  useEffect(() => {
    if (!isConnected || !mySocketId) return;
    broadcastMediaState({ isAudioEnabled, isVideoEnabled, isScreenSharing });
  }, [broadcastMediaState, isAudioEnabled, isConnected, isVideoEnabled, isScreenSharing, mySocketId, remoteParticipants.length]);

  useEffect(() => {
    if (!replaceTrack || isScreenSharing) return;
    const stream = localDisplayStream ?? cameraSourceStream;
    const videoTrack = stream?.getVideoTracks()[0];
    if (videoTrack) {
      replaceTrack(videoTrack);
    }
  }, [cameraSourceStream, isScreenSharing, localDisplayStream, replaceTrack, videoBackgroundEffect]);

  useAdaptiveVideoQuality({
    enabled: videoQualityPreference === 'auto' && isConnected && !isScreenSharing && !!localStream,
    currentQuality: autoVideoQuality,
    collectNetworkMetrics,
    onQualityChange: setAutoVideoQuality,
  });

  // Track değişikliği callback'ini bağla (ekran paylaşımı için)
  useEffect(() => {
    if (replaceTrack && setOnTrackChange) {
      setOnTrackChange((track: MediaStreamTrack) => {
        replaceTrack(track);
      });
    }
  }, [replaceTrack, setOnTrackChange]);

  const localParticipantId = mySocketId || 'local';
  const localSignals = participantSignals[localParticipantId];

  const allParticipants: Participant[] = useMemo(() => [
    {
      id: localParticipantId,
      name: userName + (isGuest ? ' (Misafir)' : ''),
      isVideoEnabled,
      isAudioEnabled,
      isScreenSharing,
      isCurrentUser: true,
      isHost: localParticipantId === hostSocketId,
      handRaised: localSignals?.handRaised ?? false,
      activeEmoji: localSignals?.activeEmoji,
      stream: localStream || undefined,
    },
    ...remoteParticipants.map((p) => {
      const media = participantMediaState[p.socketId];
      const sharing = getParticipantScreenShareState(
        p.stream,
        !!p.isScreenSharing || !!media?.isScreenSharing
      );
      const signals = participantSignals[p.socketId] ?? {};
      return {
        id: p.socketId,
        name: p.userName || 'Katılımcı',
        isVideoEnabled: media?.isVideoEnabled ?? (!!p.stream && !sharing),
        isAudioEnabled: media?.isAudioEnabled ?? true,
        isScreenSharing: sharing,
        isCurrentUser: false,
        isHost: p.socketId === hostSocketId,
        handRaised: signals.handRaised ?? false,
        activeEmoji: signals.activeEmoji,
        stream: p.stream,
      };
    }),
  ], [
    localParticipantId,
    userName,
    isGuest,
    isVideoEnabled,
    isAudioEnabled,
    isScreenSharing,
    hostSocketId,
    localSignals?.handRaised,
    localSignals?.activeEmoji,
    localStream,
    remoteParticipants,
    participantMediaState,
    participantSignals,
  ]);

  const isHandRaised = localSignals?.handRaised ?? false;

  // Sohbet kapalıyken gelen mesajlar için okunmamış sayacı
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const seenChatCountRef = useRef(0);

  useEffect(() => {
    if (showChat) {
      seenChatCountRef.current = chatMessages.length;
      setUnreadChatCount(0);
      return;
    }
    const fresh = chatMessages
      .slice(seenChatCountRef.current)
      .filter((m) => m.senderId !== mySocketId);
    if (fresh.length > 0) {
      setUnreadChatCount((c) => c + fresh.length);
    }
    seenChatCountRef.current = chatMessages.length;
  }, [chatMessages, showChat, mySocketId]);

  const handleToggleVideo = () => {
    toggleVideo();
  };

  const handleToggleAudio = () => {
    toggleAudio();
  };

  const handleToggleScreenShare = async () => {
    await toggleScreenShare();
  };

  const handleLeaveMeeting = () => {
    setShowLeaveConfirm(true);
  };

  const cancelLeave = () => {
    setShowLeaveConfirm(false);
  };

  const handleToggleParticipants = () => {
    setShowParticipants(!showParticipants);
    setShowChat(false);
    setShowAgenda(false);
  };

  const handleToggleChat = () => {
    setShowChat(!showChat);
    setShowParticipants(false);
    setShowAgenda(false);
  };

  const handleToggleAgenda = () => {
    setShowAgenda(!showAgenda);
    setShowParticipants(false);
    setShowChat(false);
  };

  const handleToggleReactions = () => {
    setShowReactions(!showReactions);
  };

  const handleToggleHandRaise = () => {
    broadcastHandRaise(!isHandRaised);
  };

  const handleSendReaction = (emoji: string) => {
    sendReaction(emoji);
  };

  const handleToggleTransparent = () => {
    setTransparentMode((prev) => !prev);
    if (!transparentMode) {
      setVideoBackgroundEffect('transparent');
    } else {
      setVideoBackgroundEffect('none');
    }
  };

  const handleToggleFullscreen = async () => {
    if (!meetingContainerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await meetingContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen hatası:', error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Ön izleme ekranı: kullanıcı katılana kadar odaya bağlanılmaz
  if (!hasJoined) {
    return (
      <PreJoin
        userName={userName}
        isGuest={isGuest}
        meetingCode={id || ''}
        stream={localStream}
        isVideoEnabled={isVideoEnabled}
        isAudioEnabled={isAudioEnabled}
        onToggleVideo={handleToggleVideo}
        onToggleAudio={handleToggleAudio}
        isOwner={isOwnerLike}
        waitingRoomEnabled={waitingRoomEnabled}
        onWaitingRoomChange={handleWaitingRoomChange}
        onJoin={() => setHasJoined(true)}
        joinDisabled={!isGuest && meetingOwnership === 'pending'}
        onCancel={performLeave}
      />
    );
  }

  // Bekleme odası: host onayı bekleniyor
  if (lobbyStatus === 'waiting') {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-purple-600/20 rounded-full blur-3xl -top-48 -left-48" />
          <div className="absolute w-96 h-96 bg-blue-600/20 rounded-full blur-3xl -bottom-48 -right-48" />
        </div>
        <div className="relative bg-slate-900/90 backdrop-blur rounded-2xl ring-1 ring-white/10 p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full border-4 border-purple-600/30 border-t-purple-500 animate-spin" />
          <h2 className="text-white text-xl font-bold mb-2">Onay bekleniyor</h2>
          <p className="text-gray-400 text-sm mb-6">
            Toplantı sahibi katılım isteğinizi inceliyor. Onaylandığında otomatik olarak
            toplantıya alınacaksınız.
          </p>
          <button
            type="button"
            onClick={performLeave}
            className="w-full px-4 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-xl font-medium transition"
          >
            Vazgeç
          </button>
        </div>
      </div>
    );
  }

  // Bekleme odası: istek reddedildi
  if (lobbyStatus === 'rejected') {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-4">
        <div className="relative bg-slate-900/90 backdrop-blur rounded-2xl ring-1 ring-white/10 p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-white text-xl font-bold mb-2">Katılım isteğiniz reddedildi</h2>
          <p className="text-gray-400 text-sm mb-6">
            Toplantı sahibi bu toplantıya katılmanızı onaylamadı.
          </p>
          <button
            type="button"
            onClick={performLeave}
            className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition"
          >
            Anasayfaya dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={meetingContainerRef}
      className={`fixed inset-0 bg-black flex flex-col ${
        transparentMode ? 'bg-black/80' : ''
      }`}
    >
      <FloatingReactions reactions={floatingReactions} />
      <ReactionBar visible={showReactions} onSendReaction={handleSendReaction} />
      {/* Misafir bildirimi */}
      {isGuest && (
        <div className="absolute top-12 right-3 z-50 bg-purple-600/90 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-2">
          <span>Misafir olarak katılıyorsunuz</span>
        </div>
      )}

      {/* Durum bildirimleri — ince header'ın hemen altında */}
      {(!isConnected || !localStream) && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-50 bg-yellow-500 text-black px-4 py-2 rounded-lg text-sm max-w-[90vw] text-center">
          {!localStream ? 'Kamera başlatılıyor...' : 'Bağlanıyor...'}
        </div>
      )}

      {isScreenSharing && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-50 bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
          Ekran paylaşılıyor
        </div>
      )}

      {isRecording && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
          Kayıt alınıyor
        </div>
      )}

      {isLimited && showWarning && remainingSeconds != null && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-50 bg-amber-500 text-black px-4 py-2 rounded-lg text-sm font-medium shadow-lg max-w-[90vw] text-center">
          Ücretsiz plan: Toplantı {Math.ceil(remainingSeconds / 60)} dakika içinde sona erecek
        </div>
      )}

      {minutesEnabled && showMinutesBanner && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-50 max-w-[92vw] px-4 py-2.5 rounded-xl bg-slate-900/95 border border-purple-500/40 text-white text-sm font-medium shadow-xl shadow-purple-900/40 text-center">
          <span>Bu toplantıda konuşmalar tutanak için otomatik yazıya dökülüyor.</span>
          {isHost && (
            <span className="block text-purple-200/90 text-xs font-normal mt-0.5">
              {minutesLastError
                ? minutesLastError
                : isTranscribing
                  ? sttAvailable === false
                    ? 'STT servisine ulaşılamıyor — tutanak yine de kaydedilecek.'
                    : 'Arka planda döküm aktif'
                  : 'Ses yakalama hazırlanıyor…'}
            </span>
          )}
        </div>
      )}

      {minutesEnabled && !showMinutesBanner && (
        <div
          className="absolute top-14 right-3 sm:right-4 z-50"
          title={
            minutesLastError
              ? minutesLastError
              : isHost && minutesTranscript.length > 0
                ? `Tutanak aktif · ${minutesTranscript.length} satır`
                : 'Tutanak aktif'
          }
        >
          <div className="w-10 h-10 rounded-xl bg-slate-900/90 border border-purple-500/50 flex items-center justify-center shadow-lg shadow-purple-900/30">
            <FileText className="w-5 h-5 text-purple-300 animate-pulse" />
          </div>
        </div>
      )}

      {/* Header — her zaman görünür */}
      <div className="flex-shrink-0 z-40">
        <Header
          meetingId={id || ''}
          duration={elapsedSeconds}
          remainingSeconds={isLimited ? remainingSeconds : null}
          isFullscreen={isFullscreen}
          onToggleFullscreen={handleToggleFullscreen}
          chatEncryptionEnabled={chatEncryptionEnabled}
          mediaEncryptionEnabled={mediaEncryptionEnabled}
        />
      </div>

      {/* Video — header ile kontrol çubuğu arasında kalan alanı doldurur */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative">
        <div
          ref={videoAreaRef}
          className={`flex-1 min-h-0 min-w-0 relative ${showChat || showParticipants || showAgenda ? 'md:w-2/3' : 'w-full'}`}
        >
          <VideoGrid
            participants={allParticipants}
            localVideoRef={localVideoRef}
            localScreenVideoRef={localScreenVideoRef}
            localCameraPreviewRef={localCameraPreviewRef}
            localCameraStream={localCameraStream}
            localScreenStream={localScreenStream}
            videoRefs={remoteVideoRefs.current}
            isVideoEnabled={isVideoEnabled}
            selectedParticipantId={selectedParticipantId}
            onSelectParticipant={setSelectedParticipantId}
            isLocalScreenSharing={isScreenSharing}
            videoBackgroundEffect={videoBackgroundEffect}
            transparentMode={transparentMode}
            localDisplayStream={localDisplayStream}
            isFullscreen={isFullscreen}
          />
          {agendaWarnSoon &&
            agendaRemainingSec > 0 &&
            agendaRemainingSec <= 10 &&
            agendaCurrentTopic && (
              <div
                className="absolute top-3 left-3 z-40 max-w-[min(100%-1.5rem,20rem)] px-3 py-2 rounded-xl bg-amber-500/95 text-black text-sm font-medium shadow-lg"
                role="status"
                aria-live="polite"
              >
                {agendaNextTopic
                  ? `"${agendaCurrentTopic.title}" bitiyor (${agendaRemainingSec} sn) — sonraki: ${agendaNextTopic.title}`
                  : `Son madde bitiyor: "${agendaCurrentTopic.title}" (${agendaRemainingSec} sn)`}
              </div>
            )}
          {isBackgroundEffectLoading && videoBackgroundEffect !== 'none' && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-xl bg-black/70 text-white text-sm">
              Arka plan efekti hazırlanıyor...
            </div>
          )}
          {backgroundEffectError && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-xl bg-red-900/80 text-white text-sm max-w-md text-center">
              {backgroundEffectError}
            </div>
          )}
        </div>

        {/* Desktop: header ile kontrol çubuğu arasında tam yükseklik */}
        {(showParticipants || showChat || showAgenda) && (
          <div className="hidden md:flex md:flex-col w-1/3 min-w-0 min-h-0 h-full flex-shrink-0 bg-slate-800/95 backdrop-blur-sm border-l border-white/10 z-30">
            {showParticipants && (
              <ParticipantsPanel
                participants={allParticipants}
                isHost={isHost}
                onMuteParticipant={muteParticipant}
                onKickParticipant={kickParticipant}
              />
            )}
            {showChat && (
              <ChatPanel
                messages={chatMessages}
                mySocketId={mySocketId}
                participants={allParticipants}
                onSendMessage={sendChatMessage}
                chatEncryptionEnabled={chatEncryptionEnabled}
                e2eRoomActive={e2eRoomActive}
                e2eReadyPeers={e2eReadyPeers}
              />
            )}
            {showAgenda && (
              <AgendaPanel
                segments={agendaSegments}
                resolved={agendaResolved}
                isHost={isHost}
                canAdvance={canAdvanceAgenda}
                onAdvance={advanceAgenda}
              />
            )}
          </div>
        )}

        {/* Mobil: video alanı üzerinde, header–kontrol arası */}
        {(showParticipants || showChat || showAgenda) && (
          <div
            className="md:hidden absolute inset-0 z-50 flex flex-col bg-slate-800/98 border-t border-white/10 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label={
              showParticipants ? 'Katılımcılar' : showChat ? 'Sohbet' : 'Gündem'
            }
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
              <h3 className="text-white font-semibold">
                {showParticipants
                  ? `Katılımcılar (${allParticipants.length})`
                  : showChat
                    ? 'Sohbet'
                    : 'Gündem'}
              </h3>
              <button
                onClick={() => {
                  setShowParticipants(false);
                  setShowChat(false);
                  setShowAgenda(false);
                }}
                className="p-2 hover:bg-white/10 active:scale-95 rounded-lg transition"
                aria-label="Kapat"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              {showParticipants && (
                <ParticipantsPanel
                  participants={allParticipants}
                  isHost={isHost}
                  onMuteParticipant={muteParticipant}
                  onKickParticipant={kickParticipant}
                />
              )}
              {showChat && (
                <ChatPanel
                  messages={chatMessages}
                  mySocketId={mySocketId}
                  participants={allParticipants}
                  onSendMessage={sendChatMessage}
                  chatEncryptionEnabled={chatEncryptionEnabled}
                  e2eRoomActive={e2eRoomActive}
                  e2eReadyPeers={e2eReadyPeers}
                />
              )}
              {showAgenda && (
                <AgendaPanel
                  segments={agendaSegments}
                  resolved={agendaResolved}
                  isHost={isHost}
                  canAdvance={canAdvanceAgenda}
                  onAdvance={advanceAgenda}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Controls — her zaman görünür */}
      <div className="flex-shrink-0 z-50 overflow-visible">
        <Controls
          isVideoEnabled={isVideoEnabled}
          isAudioEnabled={isAudioEnabled}
          isScreenSharing={isScreenSharing}
          isRecording={isRecording}
          isSavingRecording={isSavingRecording}
          isHandRaised={isHandRaised}
          isFullscreen={isFullscreen}
          transparentMode={transparentMode}
          showParticipants={showParticipants}
          showChat={showChat}
          showAgenda={showAgenda}
          hasAgenda={hasAgenda}
          unreadChatCount={unreadChatCount}
          showReactions={showReactions}
          onToggleVideo={handleToggleVideo}
          onToggleAudio={handleToggleAudio}
          onToggleScreenShare={handleToggleScreenShare}
          onToggleParticipants={handleToggleParticipants}
          onToggleChat={handleToggleChat}
          onToggleAgenda={handleToggleAgenda}
          onToggleReactions={handleToggleReactions}
          onToggleHandRaise={handleToggleHandRaise}
          onToggleRecording={toggleRecording}
          onToggleFullscreen={handleToggleFullscreen}
          onOpenBackgroundEffects={() => setShowBackgroundEffects(true)}
          onOpenSettings={() => setShowSettings(true)}
          onToggleTransparent={handleToggleTransparent}
          onLeaveMeeting={handleLeaveMeeting}
          canToggleMinutes={isHost}
          minutesEnabled={minutesEnabled}
          onToggleMinutes={handleToggleMinutes}
        />
      </div>

      {showTimeExpiredModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-slate-800 rounded-2xl border border-white/10 p-6 max-w-md mx-4 shadow-2xl text-center">
            <h3 className="text-xl font-bold text-white mb-2">Toplantı süresi doldu</h3>
            <p className="text-gray-400 mb-6">
              Ücretsiz planda toplantılar en fazla {FREE_MEETING_MAX_DURATION_MINUTES} dakika sürebilir.
              Pro plana geçerek sınırsız süre kullanabilirsiniz.
            </p>
            <button
              type="button"
              onClick={confirmLeave}
              className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition"
            >
              Toplantıdan ayrıl
            </button>
            <p className="text-gray-500 text-xs mt-3">12 saniye içinde otomatik yönlendirileceksiniz</p>
          </div>
        </div>
      )}

      {/* Tutanak açma onayı */}
      {showMinutesConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-slate-800/95 rounded-2xl border border-white/10 p-6 max-w-md w-full shadow-2xl ring-1 ring-purple-500/20">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Tutanak açılsın mı?</h3>
                <p className="text-gray-400 text-sm mt-1.5 leading-relaxed">
                  Toplantıdaki herkese kısa bir uyarı gidecek. Konuşmalar arka planda metne
                  çevrilecek; tutanak cihazınızda saklanır.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => setShowMinutesConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-xl font-medium transition"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={confirmEnableMinutes}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-medium transition shadow-lg shadow-purple-900/30"
              >
                Tutanağı aç
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toplantıdan Ayrılma Onay Modalı */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl border border-white/10 p-6 max-w-sm mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Toplantıdan Ayrıl</h3>
              <p className="text-gray-400 mb-6">Toplantıdan ayrılmak istediğinize emin misiniz?</p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelLeave}
                  className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition"
                >
                  İptal
                </button>
                <button
                  onClick={confirmLeave}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition"
                >
                  Ayrıl
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bekleme odası: katılım istekleri (host) */}
      {pendingJoinRequests.length > 0 && (
        <div className="fixed inset-x-0 top-16 sm:top-20 sm:right-4 sm:left-auto z-[70] flex flex-col items-center sm:items-end gap-2 px-4 sm:px-0 pointer-events-none">
          {pendingJoinRequests.map((req) => (
            <div
              key={req.socketId}
              className="pointer-events-auto w-full max-w-sm bg-slate-800/95 backdrop-blur rounded-2xl border border-purple-500/40 p-4 shadow-2xl join-request-enter ring-1 ring-purple-500/20"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold">
                  {(req.userName[0] || '?').toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{req.userName}</p>
                  <p className="text-gray-400 text-xs">toplantıya katılmak istiyor</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleRespondJoinRequest(req.socketId, false)}
                  className="flex-1 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-medium transition"
                >
                  Reddet
                </button>
                <button
                  type="button"
                  onClick={() => handleRespondJoinRequest(req.socketId, true)}
                  className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium transition"
                >
                  Kabul et
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <BackgroundEffectsModal
        isOpen={showBackgroundEffects}
        effect={videoBackgroundEffect}
        onClose={() => setShowBackgroundEffects(false)}
        onChange={(effect) => {
          setVideoBackgroundEffect(effect);
          setTransparentMode(effect === 'transparent');
        }}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        availableCameras={availableCameras}
        availableMicrophones={availableMicrophones}
        selectedCamera={selectedCamera}
        selectedMicrophone={selectedMicrophone}
        videoQualityPreference={videoQualityPreference}
        effectiveVideoQuality={effectiveVideoQuality}
        onCameraChange={changeCamera}
        onMicrophoneChange={changeMicrophone}
        onVideoQualityChange={setVideoQualityPreference}
      />
    </div>
  );
};

export default Meeting;